/**
 * Tela da memória de cálculo de um card de Frota (`/frota/memoria/:cardKey`).
 *
 * Mesmo formato da memória do Financeiro/Operacional — tela própria, não gaveta:
 * o que é o número, a fórmula, o recorte, o valor recalculado ao lado do que o
 * card mostrou (e se FECHA), as parcelas (cada uma abre a sua própria tela), o
 * que fica de fora e os registros que compõem o número — com busca, CSV e link
 * para o registro no SIAG.
 *
 * O recorte vem na URL (from/to + params, os mesmos que a tela de origem mandou
 * ao endpoint do card); `expected` é o número que o card exibia.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { FleetCardMemoryDto, FleetMemoryColumn, FleetMemoryUnit } from "@bi/types";
import { api, ApiError } from "../../lib/api";
import { fmtBRL, fmtNumber } from "../../lib/format";
import { useFilters, periodToDates } from "../../lib/filters";
import { IconChevronLeft, IconDownload, IconRefresh } from "../icons";
import { memoryPagePath, memoryQueryString, memoryRefFromSearch, valuesMatch, type CardMemoryRef } from "./card-memory";

/** Base da rota conforme a casca: o Portal Operacional tem a sua. */
export function memoryBasePath(pathname: string): string {
  return pathname.startsWith("/app/frota") ? "/app/frota/memoria" : "/frota/memoria";
}

export function FleetMemoryPage() {
  // Cada card/parcela é uma entrada nova no histórico: remonta do zero
  // (busca da tabela, recorte fixado, etc.).
  const location = useLocation();
  return <FleetMemoryView key={location.key} />;
}

const NATUREZA: Record<FleetCardMemoryDto["calculationType"], string> = {
  realized: "Realizado (documentos do período)",
  estimated: "Estimado (inclui parcela calculada)",
  derived: "Derivado (razão entre números da tela)",
  snapshot: "Posição atual",
  count: "Contagem",
};

function FleetMemoryView() {
  const { cardKey = "" } = useParams<{ cardKey: string }>();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { filters } = useFilters();
  const origem = useMemo(() => memoryRefFromSearch(cardKey, search), [cardKey, search]);

  // O recorte veio da tela de origem. Se o usuário trocar o período no filtro
  // global AQUI, ele passa a valer (como na memória do Financeiro) — e o número
  // do card deixa de ser comparável, então a conferência some.
  const global = periodToDates(filters.period, filters.customFrom, filters.customTo);
  const globalNaAbertura = useRef(`${global.from}|${global.to}`);
  const [fixado, setFixado] = useState(true);
  useEffect(() => {
    if (`${global.from}|${global.to}` !== globalNaAbertura.current) setFixado(false);
  }, [global.from, global.to]);

  const temPeriodo = Boolean(origem.from && origem.to);
  const memory: CardMemoryRef = fixado || !temPeriodo
    ? origem
    : { ...origem, from: global.from, to: global.to, expected: undefined };

  const qs = memoryQueryString(memory);
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["fleet-memory", cardKey, qs],
    queryFn: () => api.get<FleetCardMemoryDto>(`/fleet-memory/${encodeURIComponent(cardKey)}${qs}`),
    staleTime: 30_000,
  });

  const voltar = () => {
    // Aberta direto pelo link (sem histórico no app): volta para a frota.
    if ((window.history.state as { idx?: number } | null)?.idx) navigate(-1);
    else navigate(pathname.startsWith("/app/frota") ? "/app/frota" : "/frota");
  };
  const abrirParcela = (key: string) =>
    navigate(memoryPagePath({ cardKey: key, from: memory.from, to: memory.to, params: memory.params }, memoryBasePath(pathname)));

  const titulo = data?.title ?? memory.title ?? "Carregando…";

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* ── Cabeçalho ──────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={voltar} className="flex-shrink-0 p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-500" aria-label="Voltar">
            <IconChevronLeft className="w-5 h-5" />
          </button>
          <nav className="text-sm text-gray-400 truncate">
            <span>Frota</span>
            <span className="mx-1">›</span>
            <span>Memória de Cálculo</span>
            <span className="mx-1">›</span>
            <span className="font-medium text-gray-700">{titulo}</span>
          </nav>
        </div>
        <button className="btn-secondary flex items-center gap-1.5 flex-shrink-0" onClick={() => void refetch()} disabled={isFetching}>
          <IconRefresh className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} />
          {isFetching ? "Recalculando…" : "Recalcular"}
        </button>
      </div>

      {!fixado && temPeriodo && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-2.5 text-sm text-amber-800">
          O período foi trocado no filtro acima: a memória foi refeita no novo recorte e não é mais comparada com o
          número que o card mostrava.
        </div>
      )}

      {isLoading && (
        <div className="space-y-6 animate-pulse">
          <div className="h-40 bg-gray-100 rounded-xl" />
          <div className="h-24 bg-gray-100 rounded-xl" />
          <div className="h-64 bg-gray-100 rounded-xl" />
        </div>
      )}
      {error && (
        <div className="card p-8 text-center">
          <p className="text-gray-500">
            {error instanceof ApiError ? error.message : "Não foi possível carregar a memória de cálculo."}
          </p>
          <button className="btn-secondary mt-4" onClick={voltar}>Voltar</button>
        </div>
      )}
      {data && <MemoryContent data={data} memory={memory} onOpen={abrirParcela} />}
    </div>
  );
}

function Rotulo({ children, tone = "gray" }: { children: ReactNode; tone?: "gray" | "amber" }) {
  return (
    <p className={`text-xs font-semibold uppercase tracking-wide mb-1 ${tone === "amber" ? "text-amber-600" : "text-gray-400"}`}>
      {children}
    </p>
  );
}

function MemoryContent({ data, memory, onOpen }: {
  data: FleetCardMemoryDto;
  memory: CardMemoryRef;
  onOpen: (cardKey: string) => void;
}) {
  const expected = memory.expected;
  const fecha = expected == null || data.value == null ? null : valuesMatch(data.unit, data.value, expected);
  const periodo = data.period
    ? `${new Date(data.period.from).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" })} a ${new Date(new Date(data.period.to).getTime() - 1).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" })}`
    : null;

  return (
    <>
      {/* ── Definição ──────────────────────────────────────────────────── */}
      <div className="card p-6 space-y-4">
        <div>
          <Rotulo>O que é este número</Rotulo>
          <p className="text-sm text-gray-700 leading-relaxed">{data.definition}</p>
        </div>
        <div>
          <Rotulo>Como é calculado</Rotulo>
          <code className="block text-xs bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-gray-800 font-mono leading-relaxed whitespace-pre-wrap">
            {data.formula}
          </code>
        </div>
        <div>
          <Rotulo>Filtros aplicados</Rotulo>
          <div className="flex flex-wrap gap-2">
            {periodo && (
              <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full">
                <span className="font-medium">Período:</span> {periodo}
              </span>
            )}
            {data.filters.map((f) => (
              <span key={f} className="inline-flex items-center text-xs bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full">{f}</span>
            ))}
            {!periodo && data.filters.length === 0 && (
              <span className="text-xs text-gray-500">nenhum — posição atual da frota inteira</span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
          <div>
            <Rotulo>Campo de data</Rotulo>
            <p className="text-sm text-gray-700">{data.dateField ?? "situação atual (sem recorte de data)"}</p>
          </div>
          <div>
            <Rotulo>Natureza</Rotulo>
            <p className="text-sm text-gray-700">{NATUREZA[data.calculationType] ?? data.calculationType}</p>
          </div>
          {data.sources.length > 0 && (
            <div className="sm:col-span-2">
              <Rotulo>Origem dos dados</Rotulo>
              {data.sources.map((s) => <p key={s} className="text-xs text-gray-500">• {s}</p>)}
            </div>
          )}
          {data.limitations.length > 0 && (
            <div className="sm:col-span-2">
              <Rotulo tone="amber">O que este número não cobre</Rotulo>
              {data.limitations.map((l) => <p key={l} className="text-xs text-amber-700">⚠ {l}</p>)}
            </div>
          )}
        </div>
      </div>

      {/* ── Valor recalculado × valor do card ───────────────────────────── */}
      <div className="space-y-3">
        <div className={`grid gap-4 ${expected != null ? "sm:grid-cols-2" : ""}`}>
          <div className="card p-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Valor recalculado</p>
            {data.unavailableReason ? (
              <>
                <p className="text-3xl font-bold text-gray-400">—</p>
                <p className="text-sm text-gray-600 mt-1">{data.unavailableReason}</p>
              </>
            ) : (
              <p className="text-3xl font-bold text-gray-900">{data.valueText ?? fmtUnit(data.unit, data.value)}</p>
            )}
            {data.columns.length > 0 && (
              <p className="text-xs text-gray-400 mt-1">
                {fmtNumber(data.includedCount)} registros entram
                {data.includedCount < data.rowCount ? ` · ${fmtNumber(data.rowCount - data.includedCount)} ficam de fora` : ""}
              </p>
            )}
            <p className="text-xs text-gray-400 mt-1">Calculado em {new Date(data.generatedAt).toLocaleString("pt-BR")}</p>
          </div>
          {expected != null && (
            <div className="card p-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Valor no card</p>
              <p className="text-3xl font-bold text-gray-900">{fmtUnit(data.unit, expected)}</p>
              <p className="text-xs text-gray-400 mt-1">o número que a tela de origem exibia ao abrir</p>
            </div>
          )}
        </div>
        {fecha === true && (
          <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-green-50 text-green-700 text-sm">
            <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
            <span>Fecha com o card — a memória refaz a conta e chega no mesmo número</span>
          </div>
        )}
        {fecha === false && (
          <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-amber-50 text-amber-700 text-sm">
            <span className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0" />
            <span>Não fecha com o card — a base mudou desde que a tela carregou. Volte e atualize a tela de origem.</span>
          </div>
        )}
      </div>

      {/* ── Parcelas ───────────────────────────────────────────────────── */}
      {data.components.length > 0 && (
        <div className="card overflow-hidden">
          <div className="px-6 py-4 border-b border-surface-border">
            <p className="text-sm font-semibold text-gray-900">Parcelas</p>
            <p className="text-xs text-gray-400">as que têm memória própria abrem a sua tela</p>
          </div>
          <div className="divide-y divide-gray-100">
            {data.components.map((c) => {
              const conteudo = (
                <>
                  <span className="flex-1 text-sm text-gray-700">
                    {c.label}
                    {c.hint && <span className="block text-xs text-gray-400">{c.hint}</span>}
                  </span>
                  <span className="text-sm font-semibold text-gray-900 tabular-nums">{fmtUnit(c.unit, c.value)}</span>
                </>
              );
              return c.memoryKey ? (
                <button
                  key={c.label}
                  onClick={() => onOpen(c.memoryKey!)}
                  className="w-full flex items-center gap-3 px-6 py-3 text-left hover:bg-surface-muted"
                  title="Abrir a memória desta parcela"
                >
                  {conteudo}
                  <span className="text-gray-400">›</span>
                </button>
              ) : (
                <div key={c.label} className="flex items-center gap-3 px-6 py-3">{conteudo}<span className="w-2" /></div>
              );
            })}
          </div>
        </div>
      )}

      {data.columns.length > 0 && <MemoryTable data={data} memory={memory} />}

      {data.link && (
        <div><Link className="btn-secondary" to={data.link.href}>{data.link.label}</Link></div>
      )}

      <p className="text-xs text-gray-400 text-right">chave {data.cardKey}</p>
    </>
  );
}


function MemoryTable({ data, memory }: { data: FleetCardMemoryDto; memory: CardMemoryRef }) {
  const [busca, setBusca] = useState("");
  const [soIncluidas, setSoIncluidas] = useState(false);
  const [exportando, setExportando] = useState(false);
  const temExcluidas = data.includedCount < data.rowCount;

  const linhas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return data.rows.filter((r) =>
      (!soIncluidas || r.included) &&
      (!termo || Object.values(r.cells).some((v) => v != null && String(v).toLowerCase().includes(termo))),
    );
  }, [data.rows, busca, soIncluidas]);

  const exportar = async () => {
    setExportando(true);
    try {
      // CSV com TODAS as linhas, não só as exibidas.
      const completo = await api.get<FleetCardMemoryDto>(
        `/fleet-memory/${encodeURIComponent(data.cardKey)}${memoryQueryString(memory, { limit: "20000" })}`,
      );
      baixarCsv(completo);
    } finally {
      setExportando(false);
    }
  };

  return (
    <section className="card overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 px-6 py-4 border-b border-surface-border">
        <div className="mr-auto">
          <p className="text-sm font-semibold text-gray-900">Registros que compõem o número</p>
          <p className="text-xs text-gray-400">
            {fmtNumber(data.includedCount)} entram{temExcluidas ? ` · ${fmtNumber(data.rowCount - data.includedCount)} ficam de fora` : ""}
          </p>
        </div>
        <input className="input w-44" placeholder="Buscar" value={busca} onChange={(e) => setBusca(e.target.value)} />
        {temExcluidas && (
          <label className="inline-flex items-center gap-1.5 text-xs text-gray-600">
            <input type="checkbox" checked={soIncluidas} onChange={(e) => setSoIncluidas(e.target.checked)} />
            Só o que entra
          </label>
        )}
        <button className="btn-secondary text-xs" onClick={() => void exportar()} disabled={exportando || data.rowCount === 0}>
          <IconDownload className="w-3.5 h-3.5" /> {exportando ? "Gerando…" : "Exportar CSV"}
        </button>
      </div>

      {data.rowCount === 0 ? (
        <p className="text-sm text-gray-500 px-6 py-4">Nenhum registro no recorte — o número é zero porque não há o que somar.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-muted text-xs text-gray-500">
              <tr>
                {data.columns.map((c) => (
                  <th key={c.key} className={`px-4 py-2.5 font-medium whitespace-nowrap ${alignRight(c) ? "text-right" : "text-left"}`}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {linhas.map((r, i) => (
                <tr key={i} className={r.included ? "" : "bg-gray-50 text-gray-400"} title={r.note ?? undefined}>
                  {data.columns.map((c, j) => {
                    const texto = fmtCell(c, r.cells[c.key]);
                    return (
                      <td key={c.key} className={`px-4 py-2 whitespace-nowrap ${alignRight(c) ? "text-right tabular-nums" : ""}`}>
                        {j === 0 && r.href ? <Link className="text-tm-600 hover:underline" to={r.href}>{texto}</Link> : texto}
                        {j === 0 && r.note && <span className="block text-xs text-gray-400 whitespace-normal">{r.note}</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
            {data.total && (
              <tfoot className="border-t border-surface-border font-medium">
                <tr>
                  {data.columns.map((c, j) => (
                    <td key={c.key} className={`px-4 py-2.5 ${alignRight(c) ? "text-right tabular-nums" : ""}`}>
                      {c.key === data.total!.columnKey ? fmtCell(c, data.total!.value) : j === 0 ? "Total do que entra" : ""}
                    </td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
      {data.truncated && (
        <p className="text-xs text-gray-500 px-6 py-3 border-t border-surface-border">
          Exibindo {fmtNumber(data.rowLimit)} de {fmtNumber(data.rowCount)} registros. O total do rodapé e o valor acima
          consideram todos — use o CSV para ver a lista completa.
        </p>
      )}
    </section>
  );
}

// ── formatação ─────────────────────────────────────────────────────────────

const dec = (v: number, d: number) => v.toLocaleString("pt-BR", { minimumFractionDigits: d, maximumFractionDigits: d });

export function fmtUnit(unit: FleetMemoryUnit, v: number | null | undefined): string {
  if (v == null) return "—";
  switch (unit) {
    case "BRL": return fmtBRL(v);
    case "pct": return `${dec(v, 1)}%`;
    case "km": return `${fmtNumber(Math.round(v))} km`;
    case "liters": return `${fmtNumber(Math.round(v))} L`;
    case "km_per_liter": return `${dec(v, 2)} km/l`;
    case "brl_per_km": return `R$ ${dec(v, 2)}/km`;
    case "brl_per_liter": return `R$ ${dec(v, 3)}/L`;
    case "hours": return `${dec(v, 1)} h`;
    case "count": return fmtNumber(v);
    default: return String(v);
  }
}

const alignRight = (c: FleetMemoryColumn) => ["BRL", "number", "km", "liters", "pct", "hours", "mm"].includes(c.type);

function fmtCell(c: FleetMemoryColumn, v: string | number | boolean | null | undefined): string {
  if (v == null || v === "") return "—";
  if (typeof v === "boolean") return v ? "Sim" : "Não";
  const n = typeof v === "number" ? v : Number(v);
  switch (c.type) {
    case "BRL": return Number.isFinite(n) ? fmtBRL(n) : String(v);
    case "number": return Number.isFinite(n) ? n.toLocaleString("pt-BR", { maximumFractionDigits: 2 }) : String(v);
    case "km": return Number.isFinite(n) ? fmtNumber(Math.round(n)) : String(v);
    case "liters": return Number.isFinite(n) ? dec(n, 2) : String(v);
    case "pct": return Number.isFinite(n) ? `${dec(n, 1)}%` : String(v);
    case "hours": return Number.isFinite(n) ? dec(n, 1) : String(v);
    case "mm": return Number.isFinite(n) ? dec(n, 1) : String(v);
    case "date": return new Date(String(v)).toLocaleDateString("pt-BR", { timeZone: "UTC" });
    case "datetime": return new Date(String(v)).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
    default: return String(v);
  }
}

function baixarCsv(d: FleetCardMemoryDto) {
  const esc = (s: string) => (/[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);
  const cab = [...d.columns.map((c) => c.label), "Entra no número", "Observação"];
  const linhas = d.rows.map((r) => [
    ...d.columns.map((c) => {
      const v = r.cells[c.key];
      if (v == null) return "";
      // Número cru com vírgula decimal: abre certo no Excel em pt-BR.
      if (typeof v === "number") return String(v).replace(".", ",");
      return c.type === "date" || c.type === "datetime" ? fmtCell(c, v) : String(v);
    }),
    r.included ? "sim" : "não",
    r.note ?? "",
  ]);
  const csv = "﻿" + [cab, ...linhas].map((l) => l.map((x) => esc(String(x))).join(";")).join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `memoria-${d.cardKey.replace(/[^a-z0-9-]+/gi, "-")}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
